import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Game } from '../../../pages/lobby';
import { LiveGameItem } from '../lines/live-game-item/live-game-item';
import { QuickGameItem } from '../lines/quick-game-item/quick-game-item';
import { useQuickGames } from '../lines/quick-game-item/use-quick-games';
import { WatchGameItem } from '../lines/watch-game-iitem/watch-game-item';

import { useMyGames } from 'shared/hooks/use-build-my-games';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGamesStore } from 'shared/store/games-store';
import { RoomInfo } from 'shared/types/app-types';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import Loader from 'assets/svg/loader.svg?react';

interface LiveGamesSectionProps {
  joinRoomHandler: (roomId: number) => void;
  startGameHandler: (bet: number, wins: number, playerB: string, currency?: CurrencyType) => void;
  approveAllowanceHandler: (fullAllowance: boolean, bet: number) => void;
  games: Game[];
  activeGames: RoomInfo[];
}

//TODO: add scroll bar as in figma

export const LiveGamesSection = ({
  games,
  activeGames,
  joinRoomHandler,
  startGameHandler,
}: LiveGamesSectionProps) => {
  const publicClient = usePublicClient();
  let { address } = useAccount();
  let { startStore, activeSingle, activeRooms, clearStore } = useGamesStore();
  const myGames = useMyGames({ activeRooms, activeSingle, activeGames, address });
  const startedRef = useRef(false);

  useEffect(() => {
    if (!publicClient || startedRef.current) return;

    startedRef.current = true;
    clearStore();
    startStore(publicClient);

    return () => {
      clearStore();
      startedRef.current = false;
    };
  }, [publicClient]);

  useEffect(() => {
    return () => {
      clearStore();
    };
  }, []);

  const { t } = useTranslation();
  const { availableCurrencies } = useCurrenciesStore();
  const { chainId } = useAccount();

  const { isLoadingBalances, quickGames, selectedCurrency } = useQuickGames();

  const renderQuickGameList = useCallback(() => {
    if (isLoadingBalances) {
      return (
        <div className="flex justify-center py-4">
          <Loader className="h-6 w-6 animate-spin fill-[#fff] text-[#FFC000] sm:h-8 sm:w-8" />
        </div>
      );
    }

    return quickGames.map((game) => (
      <QuickGameItem
        key={game.currency.symbol + game.amount + game.wins}
        amount={Number(game.amount)}
        currency={game.currency}
        wins={game.wins}
        startGameHandler={startGameHandler}
      />
    ));
  }, [address, quickGames, availableCurrencies, isLoadingBalances, chainId, startGameHandler]);

  const renderGamesList = useCallback(() => {
    return activeSingle.map((room) => (
      <LiveGameItem
        key={`${room.data.roomId.toString()}_${room.data.playerA}`}
        roomId={room.data.roomId}
        address={room.data.playerA as `0x${string}`}
        amount={Number(room.data.bet)}
        currency={availableCurrencies.find(
          (currency) => currency.address.toLowerCase() === room.data.token.toLowerCase(),
        )}
        ourGame={room.data.playerA?.toLowerCase() === address?.toLowerCase()}
        joinRoomHandler={joinRoomHandler}
        status={room.status}
        wins={room.data.wins}
      />
    ));
  }, [address, availableCurrencies, isLoadingBalances, chainId, activeSingle, joinRoomHandler]);

  const renderMyGamesList = useCallback(() => {
    if (myGames.length === 0) return null;
    return myGames.map((room) => (
      <LiveGameItem
        key={`${room.data.roomId.toString()}_${room.data.playerA}`}
        roomId={room.data.roomId}
        address={room.data.playerA as `0x${string}`}
        amount={Number(room.data.bet)}
        currency={availableCurrencies.find(
          (currency) => currency.address.toLowerCase() === room.data.token.toLowerCase(),
        )}
        ourGame
        status={room.status}
        joinRoomHandler={joinRoomHandler}
        wins={room.data.wins}
      />
    ));
  }, [
    address,
    availableCurrencies,
    isLoadingBalances,
    selectedCurrency,
    chainId,
    myGames,
    joinRoomHandler,
  ]);

  const renderWatchGamesList = useCallback(
    () => games.map((game) => <WatchGameItem key={game[0]} game={game} />),
    [games],
  );

  return (
    <div className="flex min-w-[55%] flex-1 flex-col xlg:max-w-[74rem]">
      <div className="">
        <div className="gradient-title text-[20px] font-bold lg:text-[52px]">
          {t('pages.liveGamesSection.title')}
        </div>
      </div>

      <div className="mt-6 flex max-h-[686px] flex-1 flex-col overflow-hidden rounded-3xl border border-border bg-[linear-gradient(0deg,#080540,#080540),radial-gradient(50%_50%_at_50%_50%,rgba(0,11,255,0.3)_0%,rgba(1,0,29,0.3)_100%)] p-6 lg:border-[3px]">
        <div className="scroll-custom flex flex-col gap-2 overflow-y-auto pr-2 sm:gap-4">
          {renderQuickGameList()}
          {renderMyGamesList()}
          {renderGamesList()}
          {games?.length > 0 && (
            <div className="flex items-center justify-center">
              <div className="h-[1px] w-full bg-white/15" />
              <p className="scroll-custom whitespace-nowrap bg-gradient-to-tr from-white via-white to-[#B5E0FF] bg-clip-text px-2.5 text-base font-bold text-transparent text-white/50">
                {t('pages.liveGamesSection.watchGames')}
              </p>
              <div className="h-[1px] w-full bg-white/15" />
            </div>
          )}
          {games?.length > 0 && renderWatchGamesList()}
        </div>
      </div>
    </div>
  );
};
